import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@futuredev/shared-auth';
import api from '@futuredev/shared-auth/api';
import { useTheme } from '../../contexts/ThemeContext';
import BackLink from '../../components/BackLink';
import { extractApiError } from '@futuredev/shared-system/formatters';
import { TableScroll } from '@futuredev/shared-components';
import {
  GlobeAltIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  UserIcon,
  BuildingOfficeIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowPathIcon,
  DocumentDuplicateIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  UserGroupIcon,
  ArrowTopRightOnSquareIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';

interface Website {
  id: number;
  name: string;
  slug: string;
  status: string;
  created_at: string;
  updated_at: string;
  company_id: number | null;
  company_name: string | null;
  assigned_to_id: number | null;
  assigned_to_name: string | null;
  page_count: number;
  domain: string | null;
}

const SORTABLE_WEBSITE_KEYS = ['updated_at', 'created_at', 'name', 'status'] as const;
type SortableWebsiteKey = typeof SORTABLE_WEBSITE_KEYS[number];
const isSortableWebsiteKey = (v: string): v is SortableWebsiteKey =>
  (SORTABLE_WEBSITE_KEYS as readonly string[]).includes(v);

interface Member {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  active: boolean;
}

interface Company {
  id: number;
  name: string;
  active: boolean;
}

interface WebsiteStats {
  total: number;
  published: number;
  draft: number;
  assigned: number;
  unassigned: number;
}

function WebsiteManager(): React.ReactElement {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useTheme();

  const [websites, setWebsites] = useState<Website[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [assignmentFilter, setAssignmentFilter] = useState<string>('all');
  const [companyFilter, setCompanyFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<SortableWebsiteKey>('updated_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const [pageSize, setPageSize] = useState<number | 'all'>(25);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const [showAssignModal, setShowAssignModal] = useState<boolean>(false);
  const [selectedWebsite, setSelectedWebsite] = useState<Website | null>(null);
  const [assignmentType, setAssignmentType] = useState<string>('member');
  const [selectedAssignee, setSelectedAssignee] = useState<string>('');
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [websiteToDelete, setWebsiteToDelete] = useState<Website | null>(null);

  const [stats, setStats] = useState<WebsiteStats>({
    total: 0,
    published: 0,
    draft: 0,
    assigned: 0,
    unassigned: 0,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const [websitesRes, membersRes, companiesRes] = await Promise.all([
        api.get('/websites/admin/all'),
        api.get('/user/list'),
        api.get('/companies/list'),
      ]);

      const websiteData: Website[] = websitesRes.data?.data || [];
      const memberData: Member[] = membersRes.data?.data || [];
      const companyData: Company[] = companiesRes.data?.data || [];

      setWebsites(websiteData);
      setMembers(memberData);
      setCompanies(companyData);

      setStats({
        total: websiteData.length,
        published: websiteData.filter(w => w.status === 'published').length,
        draft: websiteData.filter(w => w.status === 'draft').length,
        assigned: websiteData.filter(w => w.assigned_to_id || w.company_id).length,
        unassigned: websiteData.filter(w => !w.assigned_to_id && !w.company_id).length,
      });

    } catch (err: unknown) {
      console.error('Load data failed:', err);
      setError(extractApiError(err, 'Failed to load data'));

      setWebsites([
        { id: 1, name: 'Apex Tech', slug: 'apex-tech', status: 'published', created_at: '2025-01-15T10:00:00Z', updated_at: '2025-01-20T14:30:00Z', company_id: 1, company_name: 'TechCorp Inc', assigned_to_id: null, assigned_to_name: null, page_count: 5, domain: 'apextech.com' },
        { id: 2, name: 'Creative Studio', slug: 'creative-studio', status: 'draft', created_at: '2025-01-18T09:00:00Z', updated_at: '2025-01-18T09:00:00Z', company_id: null, company_name: null, assigned_to_id: 5, assigned_to_name: 'John Designer', page_count: 2, domain: null },
        { id: 3, name: 'Launch Labs', slug: 'launch-labs', status: 'draft', created_at: '2025-01-20T11:00:00Z', updated_at: '2025-01-20T11:00:00Z', company_id: null, company_name: null, assigned_to_id: null, assigned_to_name: null, page_count: 1, domain: null },
      ]);
      setStats({ total: 3, published: 1, draft: 2, assigned: 2, unassigned: 1 });
    } finally {
      setLoading(false);
    }
  };

  const filteredWebsites = useMemo((): Website[] => {
    let filtered = [...websites];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(w =>
        w.name?.toLowerCase().includes(query) ||
        w.slug?.toLowerCase().includes(query) ||
        w.domain?.toLowerCase().includes(query) ||
        w.company_name?.toLowerCase().includes(query) ||
        w.assigned_to_name?.toLowerCase().includes(query)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(w => w.status === statusFilter);
    }

    if (assignmentFilter === 'assigned') {
      filtered = filtered.filter(w => w.assigned_to_id || w.company_id);
    } else if (assignmentFilter === 'unassigned') {
      filtered = filtered.filter(w => !w.assigned_to_id && !w.company_id);
    }

    if (companyFilter !== 'all') {
      filtered = filtered.filter(w => w.company_id === parseInt(companyFilter));
    }

    filtered.sort((a, b) => {
      let aVal: string | number = a[sortBy] ?? '';
      let bVal: string | number = b[sortBy] ?? '';

      if (sortBy === 'updated_at' || sortBy === 'created_at') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

    return filtered;
  }, [websites, searchQuery, statusFilter, assignmentFilter, companyFilter, sortBy, sortOrder]);

  const paginatedWebsites = useMemo((): Website[] => {
    if (pageSize === 'all') return filteredWebsites;
    const start = (currentPage - 1) * (pageSize as number);
    return filteredWebsites.slice(start, start + (pageSize as number));
  }, [filteredWebsites, currentPage, pageSize]);

  const totalPages = pageSize === 'all' ? 1 : Math.ceil(filteredWebsites.length / (pageSize as number));

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const getStatusBadge = (status: string): React.ReactElement => {
    switch (status) {
      case 'published':
        return (<span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"><CheckCircleIcon className="w-3 h-3" />Published</span>);
      case 'draft':
        return (<span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"><ClockIcon className="w-3 h-3" />Draft</span>);
      case 'archived':
        return (<span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600"><XCircleIcon className="w-3 h-3" />Archived</span>);
      default:
        return (<span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{status}</span>);
    }
  };

  const handleAssign = async (): Promise<void> => {
    if (!selectedWebsite || !selectedAssignee) return;
    try {
      await api.put(`/websites/admin/${selectedWebsite.id}/assign`, {
        assignmentType,
        assigneeId: parseInt(selectedAssignee),
      });
      setShowAssignModal(false);
      setSelectedWebsite(null);
      setSelectedAssignee('');
      loadData();
    } catch (err: unknown) {
      console.error('Assign website failed:', err);
      alert('Failed to assign website: ' + (extractApiError(err)));
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!websiteToDelete) return;
    try {
      await api.delete(`/websites/admin/${websiteToDelete.id}`);
      setShowDeleteModal(false);
      setWebsiteToDelete(null);
      loadData();
    } catch (err: unknown) {
      console.error('Delete website failed:', err);
      alert('Failed to delete website: ' + (extractApiError(err)));
    }
  };

  const handleDuplicate = async (website: Website): Promise<void> => {
    try {
      await api.post(`/websites/admin/${website.id}/duplicate`);
      loadData();
    } catch (err: unknown) {
      console.error('Duplicate website failed:', err);
      alert('Failed to duplicate website: ' + (extractApiError(err)));
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900' : ''}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className={`mt-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Loading websites...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen py-8 ${isDark ? 'bg-gray-900 text-white' : ''}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <BackLink to="/admin" label="Back to Back Office" />

        <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className={`text-3xl font-bold flex items-center gap-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <GlobeAltIcon className="w-8 h-8 text-primary-600" />
              Website Manager
            </h1>
            <p className={`mt-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Manage all websites across the platform</p>
          </div>
          <div className="flex gap-3">
            <button onClick={loadData} className={`flex items-center gap-2 px-4 py-2 border rounded-lg ${isDark ? 'border-gray-600 hover:bg-gray-700 text-gray-300' : 'border-gray-300 hover:bg-gray-50'}`}>
              <ArrowPathIcon className="w-5 h-5" />Refresh
            </button>
            <Link to="/admin/website-builder/ai" className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:opacity-90">
              <SparklesIcon className="w-5 h-5" />Create with AI
            </Link>
            <Link to="/admin/website-builder/new" className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
              <PlusIcon className="w-5 h-5" />New Website
            </Link>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Total Sites', value: stats.total, icon: GlobeAltIcon, color: isDark ? 'text-white' : 'text-gray-900', iconColor: 'text-gray-400' },
            { label: 'Published', value: stats.published, icon: CheckCircleIcon, color: 'text-green-600', iconColor: 'text-green-500' },
            { label: 'Drafts', value: stats.draft, icon: ClockIcon, color: 'text-yellow-600', iconColor: 'text-yellow-500' },
            { label: 'Assigned', value: stats.assigned, icon: UserGroupIcon, color: 'text-blue-600', iconColor: 'text-blue-500' },
            { label: 'Unassigned', value: stats.unassigned, icon: ExclamationTriangleIcon, color: 'text-orange-600', iconColor: 'text-orange-500' },
          ].map((stat) => (
            <div key={stat.label} className={`rounded-xl shadow-sm p-4 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
              <div className="flex items-center justify-between">
                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{stat.label}</span>
                <stat.icon className={`w-5 h-5 ${stat.iconColor}`} />
              </div>
              <p className={`text-2xl font-bold mt-1 ${stat.color}`}>{stat.value}</p>
            </div>
          ))}
        </div>

        <div className={`mt-6 rounded-xl shadow-sm p-4 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
              <input type="text" value={searchQuery} onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setSearchQuery(e.target.value); setCurrentPage(1); }} className={`w-full pl-10 pr-4 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'} focus:ring-2 focus:ring-primary-500 focus:border-primary-500`} placeholder="Search by name, domain, company, or member..." />
            </div>
            <select value={statusFilter} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => { setStatusFilter(e.target.value); setCurrentPage(1); }} className={`px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}>
              <option value="all">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
            <select value={assignmentFilter} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => { setAssignmentFilter(e.target.value); setCurrentPage(1); }} className={`px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}>
              <option value="all">All Assignments</option>
              <option value="assigned">Assigned</option>
              <option value="unassigned">Unassigned</option>
            </select>
            <select value={companyFilter} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => { setCompanyFilter(e.target.value); setCurrentPage(1); }} className={`px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}>
              <option value="all">All Companies</option>
              {companies.map(company => (<option key={company.id} value={company.id}>{company.name}</option>))}
            </select>
            <select value={`${sortBy}-${sortOrder}`} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              const [field, order] = e.target.value.split('-');
              if (isSortableWebsiteKey(field)) setSortBy(field);
              if (order === 'asc' || order === 'desc') setSortOrder(order);
            }} className={`px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}>
              <option value="updated_at-desc">Last Updated (Newest)</option>
              <option value="updated_at-asc">Last Updated (Oldest)</option>
              <option value="created_at-desc">Created (Newest)</option>
              <option value="created_at-asc">Created (Oldest)</option>
              <option value="name-asc">Name (A-Z)</option>
              <option value="name-desc">Name (Z-A)</option>
            </select>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Showing {paginatedWebsites.length} of {filteredWebsites.length} websites
            {filteredWebsites.length !== websites.length && ` (filtered from ${websites.length} total)`}
          </p>
          <div className="flex items-center gap-2">
            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Show:</span>
            <select value={pageSize} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => { setPageSize(e.target.value === 'all' ? 'all' : parseInt(e.target.value)); setCurrentPage(1); }} className={`px-2 py-1 border rounded text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value="all">All</option>
            </select>
          </div>
        </div>

        <div className={`mt-4 rounded-xl shadow-sm overflow-hidden ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
          {paginatedWebsites.length === 0 ? (
            <div className="p-12 text-center">
              <GlobeAltIcon className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>No websites found</h3>
              <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {searchQuery || statusFilter !== 'all' || assignmentFilter !== 'all' || companyFilter !== 'all' ? 'Try adjusting your filters' : 'Create your first website to get started'}
              </p>
              {!searchQuery && statusFilter === 'all' && (
                <Link to="/admin/website-builder/ai" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:opacity-90">
                  <SparklesIcon className="w-5 h-5" />Create with AI
                </Link>
              )}
            </div>
          ) : (
            <TableScroll>
<table className={`min-w-full divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
              <thead className={isDark ? 'bg-gray-700' : 'bg-gray-50'}>
                <tr>
                  {['Website', 'Status', 'Assigned To', 'Pages', 'Last Updated', 'Actions'].map((h, i) => (
                    <th key={h} className={`px-6 py-3 ${i === 5 ? 'text-right' : 'text-left'} text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {paginatedWebsites.map((website) => (
                  <tr key={website.id} className={isDark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center text-white font-bold">
                          {website.name?.charAt(0) || 'W'}
                        </div>
                        <div className="ml-4">
                          <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{website.name}</div>
                          <div className="text-sm">
                            {website.domain ? (
                              <a href={`https://${website.domain}`} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline flex items-center gap-1">
                                {website.domain}<ArrowTopRightOnSquareIcon className="w-3 h-3" />
                              </a>
                            ) : (
                              <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>/{website.slug}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(website.status)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {website.company_name ? (
                        <div className="flex items-center gap-2"><BuildingOfficeIcon className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} /><span className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{website.company_name}</span></div>
                      ) : website.assigned_to_name ? (
                        <div className="flex items-center gap-2"><UserIcon className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} /><span className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{website.assigned_to_name}</span></div>
                      ) : (
                        <button onClick={() => { setSelectedWebsite(website); setShowAssignModal(true); }} className="text-sm text-orange-600 hover:text-orange-700 font-medium flex items-center gap-1">
                          <ExclamationTriangleIcon className="w-4 h-4" />Unassigned
                        </button>
                      )}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{website.page_count || 0} pages</td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{formatDate(website.updated_at)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <div className="flex items-center justify-end gap-2">
                        <Link to={`/admin/website-builder/${website.id}`} className={`p-2 rounded-lg ${isDark ? 'text-gray-400 hover:text-primary-400 hover:bg-primary-900/30' : 'text-gray-400 hover:text-primary-600 hover:bg-primary-50'}`} title="Edit"><PencilIcon className="w-4 h-4" /></Link>
                        <button onClick={() => window.open(`/preview/${website.slug}`, '_blank')} className={`p-2 rounded-lg ${isDark ? 'text-gray-400 hover:text-green-400 hover:bg-green-900/30' : 'text-gray-400 hover:text-green-600 hover:bg-green-50'}`} title="Preview"><EyeIcon className="w-4 h-4" /></button>
                        <button onClick={() => handleDuplicate(website)} className={`p-2 rounded-lg ${isDark ? 'text-gray-400 hover:text-blue-400 hover:bg-blue-900/30' : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'}`} title="Duplicate"><DocumentDuplicateIcon className="w-4 h-4" /></button>
                        <button onClick={() => { setSelectedWebsite(website); setShowAssignModal(true); }} className={`p-2 rounded-lg ${isDark ? 'text-gray-400 hover:text-purple-400 hover:bg-purple-900/30' : 'text-gray-400 hover:text-purple-600 hover:bg-purple-50'}`} title="Assign"><UserGroupIcon className="w-4 h-4" /></button>
                        <button onClick={() => { setWebsiteToDelete(website); setShowDeleteModal(true); }} className={`p-2 rounded-lg ${isDark ? 'text-gray-400 hover:text-red-400 hover:bg-red-900/30' : 'text-gray-400 hover:text-red-600 hover:bg-red-50'}`} title="Delete"><TrashIcon className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
</TableScroll>
          )}
        </div>

        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className={`flex items-center gap-1 px-3 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${isDark ? 'border-gray-600 hover:bg-gray-700 text-gray-300' : 'border-gray-300 hover:bg-gray-50'}`}>
              <ChevronLeftIcon className="w-4 h-4" />Previous
            </button>
            <div className="flex items-center gap-2">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) { pageNum = i + 1; }
                else if (currentPage <= 3) { pageNum = i + 1; }
                else if (currentPage >= totalPages - 2) { pageNum = totalPages - 4 + i; }
                else { pageNum = currentPage - 2 + i; }
                return (
                  <button key={pageNum} onClick={() => setCurrentPage(pageNum)} className={`w-8 h-8 rounded-lg ${currentPage === pageNum ? 'bg-primary-600 text-white' : (isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100')}`}>{pageNum}</button>
                );
              })}
            </div>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className={`flex items-center gap-1 px-3 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${isDark ? 'border-gray-600 hover:bg-gray-700 text-gray-300' : 'border-gray-300 hover:bg-gray-50'}`}>
              Next<ChevronRightIcon className="w-4 h-4" />
            </button>
          </div>
        )}

        {stats.unassigned > 0 && (
          <div className={`mt-6 border rounded-xl p-4 ${isDark ? 'bg-orange-900/30 border-orange-700' : 'bg-orange-50 border-orange-200'}`}>
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="w-6 h-6 text-orange-600 flex-shrink-0" />
              <div>
                <h3 className={`font-semibold ${isDark ? 'text-orange-300' : 'text-orange-900'}`}>{stats.unassigned} Unassigned Website{stats.unassigned > 1 ? 's' : ''}</h3>
                <p className={`text-sm mt-1 ${isDark ? 'text-orange-400' : 'text-orange-700'}`}>These websites need to be assigned to a member or company before they can be published.</p>
                <button onClick={() => { setAssignmentFilter('unassigned'); setCurrentPage(1); }} className={`mt-2 text-sm font-medium hover:underline ${isDark ? 'text-orange-300' : 'text-orange-700'}`}>View unassigned websites &rarr;</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showAssignModal && selectedWebsite && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-xl shadow-2xl max-w-md w-full ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="p-6">
              <h2 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Assign Website</h2>
              <p className={`text-sm mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Assign &quot;{selectedWebsite.name}&quot; to a member or company</p>
              <div className="mb-4">
                <div id="website-manager-assign-to" className={`field-caption block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Assign to:</div>
                <div role="group" aria-labelledby="website-manager-assign-to" className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="assignmentType" value="member" checked={assignmentType === 'member'} onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setAssignmentType(e.target.value); setSelectedAssignee(''); }} className="text-primary-600" />
                    <UserIcon className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />Member
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="assignmentType" value="company" checked={assignmentType === 'company'} onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setAssignmentType(e.target.value); setSelectedAssignee(''); }} className="text-primary-600" />
                    <BuildingOfficeIcon className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />Company
                  </label>
                </div>
              </div>
              <div className="mb-6">
                <label htmlFor="website-manager-selected-assignee" className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Select {assignmentType === 'member' ? 'Member' : 'Company'}:</label>
                <select id="website-manager-selected-assignee" value={selectedAssignee} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedAssignee(e.target.value)} className={`w-full px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}>
                  <option value="">Select...</option>
                  {assignmentType === 'member' ? (
                    members.filter(m => m.active !== false).map(member => (<option key={member.id} value={member.id}>{member.first_name} {member.last_name} ({member.email})</option>))
                  ) : (
                    companies.filter(c => c.active !== false).map(company => (<option key={company.id} value={company.id}>{company.name}</option>))
                  )}
                </select>
              </div>
              <div className={`border rounded-lg p-3 mb-6 ${isDark ? 'bg-blue-900/30 border-blue-700' : 'bg-blue-50 border-blue-200'}`}>
                <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-700'}`}><strong>Note:</strong> Only assigned websites can be published to production.</p>
              </div>
            </div>
            <div className={`px-6 py-4 rounded-b-xl flex justify-end gap-3 ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <button onClick={() => { setShowAssignModal(false); setSelectedWebsite(null); setSelectedAssignee(''); }} className={`px-4 py-2 border rounded-lg ${isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-600' : 'border-gray-300 hover:bg-gray-100'}`}>Cancel</button>
              <button onClick={handleAssign} disabled={!selectedAssignee} className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed">Assign Website</button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && websiteToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-xl shadow-2xl max-w-md w-full ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="p-6">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4"><TrashIcon className="w-6 h-6 text-red-600" /></div>
              <h2 className={`text-xl font-bold text-center mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Delete Website</h2>
              <p className={`text-sm text-center mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Are you sure you want to delete &quot;{websiteToDelete.name}&quot;? This action cannot be undone.</p>
              {websiteToDelete.status === 'published' && (
                <div className={`border rounded-lg p-3 ${isDark ? 'bg-red-900/30 border-red-700' : 'bg-red-50 border-red-200'}`}>
                  <p className={`text-sm ${isDark ? 'text-red-300' : 'text-red-700'}`}><strong>Warning:</strong> This is a published website. Deleting it will take it offline immediately.</p>
                </div>
              )}
            </div>
            <div className={`px-6 py-4 rounded-b-xl flex justify-end gap-3 ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <button onClick={() => { setShowDeleteModal(false); setWebsiteToDelete(null); }} className={`px-4 py-2 border rounded-lg ${isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-600' : 'border-gray-300 hover:bg-gray-100'}`}>Cancel</button>
              <button onClick={handleDelete} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Delete Website</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WebsiteManager;
